"use client";

import * as React from "react";
import Image from "next/image";
import Dialog from "src/components/Dialog";
import AsoebiModal from "@src/components/AsoebiModal";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import {
  EventBySlugQuery,
  GetAlbumDetailsQuery,
  GetHeroImageQuery,
} from "@src/@types/api.d";
import defaultImg from "../../../../public/images/bw-flower.jpeg";
import { AvailablePaymentCurrencies } from "@src/components/BirthdayTemplate1/AsoEbiLink";

interface Props {
  eventUuid: string | undefined;
  eventId: number;
  showAsoebi?: boolean;
  showCashGift?: boolean;
  isCashGiftSetup: boolean;
  availableGiftPaymentCurrencies?: AvailablePaymentCurrencies;
  albumDetailsData?: GetAlbumDetailsQuery | null;
  heroImageData?: GetHeroImageQuery | null;
  eventBySlugData?: EventBySlugQuery | null;
}

const AsoEbi: React.FC<Props> = ({
  eventUuid,
  showAsoebi,
  albumDetailsData,
  heroImageData,
  eventBySlugData,
}) => {
  const [openAsoebi, setOpenAsoebi] = React.useState(false);

  const validAlbumImages = React.useMemo(() => {
    return (albumDetailsData?.getMedia ?? []).filter(
      (m) =>
        m &&
        typeof m?.type === "string" &&
        m.type.toLowerCase().includes("image") &&
        m?.meta?.s3_url?.startsWith("http")
    );
  }, [albumDetailsData]);

  const leftImage =
    validAlbumImages[0]?.meta?.s3_url ||
    heroImageData?.getHeroImage ||
    eventBySlugData?.eventBySlug?.hero_image_thumbnail ||
    defaultImg;

  const rightImage =
    validAlbumImages[1]?.meta?.s3_url ||
    eventBySlugData?.eventBySlug?.pass_image ||
    defaultImg;

  if (!showAsoebi) {
    return null;
  }

  return (
    <section className="w-full py-10 md:py-16 min-h-[calc(105vh-var(--t22-header-height))] bg-[#FAF7F5] relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_8%,rgba(34,139,34,0.08),transparent_30%),radial-gradient(circle_at_82%_18%,rgba(28,24,32,0.05),transparent_28%)]" />
      <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-20">
        <div className="mb-12 text-center relative z-10 md:mb-16">
          <span className="mb-4 block font-jost text-[0.62rem] font-medium uppercase tracking-[0.35em] text-[#228B22]">
            Aso Ebi
          </span>
          <h2 className="font-corGaramond text-[clamp(3rem,7vw,5.4rem)] font-normal leading-[1.02] text-[#401d15]">
            Celebrate{" "}
            <em className="font-light italic text-[#228B22]">in Style</em>
          </h2>
          <div className="mt-5 flex items-center justify-center gap-4">
            <span className="h-px w-[56px] bg-gradient-to-r from-transparent to-[#228B22]" />
            <span className="h-1.5 w-1.5 rotate-45 bg-[#228B22]" />
            <span className="h-px w-[56px] bg-gradient-to-r from-[#228B22] to-transparent" />
          </div>
        </div>

        <article className="border border-[#228B22]/12 p-2 relative z-10">
          <div className="bg-white/80 border border-[#228B22]/18 shadow-[0_24px_70px_rgba(28,24,32,0.08)] flex flex-col lg:flex-row overflow-hidden">
            <div className="lg:w-[45%] p-6 border-b lg:border-b-0 lg:border-r border-[#228B22]/10">
              <div className="grid grid-cols-2 gap-4 md:gap-6">
                <div className="relative w-full h-56 md:h-72 overflow-hidden rounded-[40%_60%_60%_40%/40%_40%_60%_60%]">
                  <Image
                    src={leftImage}
                    alt="Couple photo one"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center top"
                    placeholder="blur"
                    blurDataURL={`data:image/svg+xml;base64,${toBase64(
                      shimmer(360, 520)
                    )}`}
                    onError={(event) => {
                      const target = event.currentTarget as HTMLImageElement;
                      if (!target.src.includes("bw-flower.jpeg")) {
                        target.src = defaultImg.src;
                      }
                    }}
                  />
                </div>
                <div className="relative w-full h-56 md:h-72 overflow-hidden rounded-[60%_40%_40%_60%/40%_40%_60%_60%] mt-8 md:mt-10">
                  <Image
                    src={rightImage}
                    alt="Couple photo two"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center top"
                    placeholder="blur"
                    blurDataURL={`data:image/svg+xml;base64,${toBase64(
                      shimmer(360, 520)
                    )}`}
                    onError={(event) => {
                      const target = event.currentTarget as HTMLImageElement;
                      if (!target.src.includes("bw-flower.jpeg")) {
                        target.src = defaultImg.src;
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="lg:w-[55%] p-6 md:p-8 flex flex-col justify-center gap-6">
              <div className="bg-white rounded-xl py-5 md:py-6">
                <h3 className="text-3xl md:text-4xl text-[#401d15] font-corGaramond leading-tight mb-3">
                  Celebrate with us in style!
                </h3>
                <p className="text-lg md:text-xl text-[#401d15]/62 font-corGaramond font-light leading-[1.7]">
                  Explore our curated Asoebi options and place your order for
                  the big day.
                </p>
              </div>

              <Dialog
                trigger={
                  <button
                    className="inline-flex items-center justify-center px-6 py-3 bg-transparent text-[#401d15] border border-[#401d15] font-jost uppercase tracking-[0.2em] text-[0.58rem] hover:bg-[#401d15] hover:text-white transition-all duration-300"
                    type="button"
                  >
                    Get Your Asoebi
                  </button>
                }
                open={openAsoebi}
                onOpenChange={setOpenAsoebi}
              >
                <AsoebiModal eventUuid={eventUuid} setOpen={setOpenAsoebi} />
              </Dialog>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
};

export default AsoEbi;
